import {
  actionCardCommands as cardWrites,
  canOpenCodingAgent,
  canStartCodingAgent,
  CodingAgentContentSchema,
  codingAgentSessionToContinue,
  isCodingAgentRunning,
  type CodingAgentContent,
  type CodingAgentRun,
} from '@littlebird/action-cards';
import { globalEventBus, EventNames } from '@littlebird/event-bus';
import { observable, runInAction } from 'mobx';
import { toast } from 'sonner';

import { electronApis } from '@/features/electron-api';
import type { AgentSession } from '@/features/electron-api/features/CodingAgentsAPI';
import { logError } from '@/lib/errorLogging';
import { littlebirdSdk } from '@/lib/sdk-init';
import { inElectron } from '@/utils/capabilities';

import { actionCardCommands } from './commands';

import type { ActionCard } from '@littlebird/sdk';

// The main process owns a run while it lasts and reports it by the card's id. The run on the card
// is the stored copy of that report, which other devices and the backend read

const CLOSED_BEFORE_FINISH =
  'Littlebird closed before this run finished. Start it again to continue the session.';

/** Cards whose agent is being started in this window */
export const startingCards = observable.set<string>();
/** What the user agreed to for one start: to start beside another agent, and to trust the folder in the agent */
export interface StartChoices {
  startAnyway?: boolean;
  trustFolder?: boolean;
}

/** How a start was asked for: the choices made, and whether it starts a new session instead of continuing one */
type StartOptions = StartChoices & { newSession?: boolean };

/**
 * A start that waits on the user: what to tell them, the choice that lets it go on, and how the start was asked for,
 * which the next start keeps, so a folder that is busy and untrusted asks once for each, and a new session stays new
 */
export interface StartQuestion {
  error: string;
  choice: keyof StartChoices;
  given: StartOptions;
}

/** Cards whose start waits on the user */
export const startQuestions = observable.map<string, StartQuestion>();

export const codingAgentContentOf = (
  card: ActionCard
): CodingAgentContent | null =>
  card.actionType === 'coding_agent'
    ? (CodingAgentContentSchema.safeParse(card.content).data ?? null)
    : null;

const codingCard = (cardId: string) => {
  const card = littlebirdSdk.stores.actionCards.get(cardId);
  const content = card && !card.deletedAt && codingAgentContentOf(card);
  return content ? { card, content } : undefined;
};

/** The card while it still waits for the user, which is the only time its run changes */
const pendingCard = (cardId: string) => {
  const found = codingCard(cardId);
  return found?.card.status === 'draft' ? found : undefined;
};

const deviceId = observable.box<string>();
let device: Promise<string> | undefined;
const thisDevice = () =>
  (device ??= electronApis.system.getDeviceId().then((id) => {
    runInAction(() => deviceId.set(id));
    return id;
  }));

/** Whether the card's run is on another computer, which is the only one that can tell how it goes or stop it */
export const runsElsewhere = (run: CodingAgentRun): boolean =>
  Boolean(run.device_id && deviceId.get() && run.device_id !== deviceId.get());

// A second write to a card is skipped while one is under way, so writes wait for each other
let writes: Promise<unknown> = Promise.resolve();
const inTurn = <T>(write: () => Promise<T>): Promise<T> => {
  const next = writes.then(write, write);
  writes = next.catch(() => undefined);
  return next;
};

// A run, and the session it ran when that is known, which the card keeps as its `session_id`
type Run = Omit<CodingAgentRun, 'device_id'> & { session_id?: string };

// The card is read inside the turn, so the run is worked out from what the writes before it left,
// and an edit the user made meanwhile is kept
const saveRun = (
  cardId: string,
  next: (content: CodingAgentContent) => Run | null
) =>
  inTurn(async () => {
    const content = pendingCard(cardId)?.content;
    const write = content && next(content);
    if (!content || !write) return;
    const { session_id: sessionId = content.session_id, ...run } = write;
    const stored = content.run;
    const same =
      stored?.state === run.state &&
      content.session_id === sessionId &&
      stored?.error === run.error;
    if (same) return;
    const device_id = await thisDevice();
    const withRun = {
      ...content,
      session_id: sessionId,
      run: { ...run, device_id },
    };
    const saved = await cardWrites.saveDraft(cardId, withRun);
    // A card is done only once its finished run is on it
    if (saved.status === 'succeeded' && run.state === 'finished') {
      await cardWrites.acknowledge(cardId);
    }
  });

/** Saves a change the user made to the card, in turn with the run's own writes */
export const saveCodingAgentEdit = (
  cardId: string,
  change: Partial<Pick<CodingAgentContent, 'cwd' | 'access'>>
) => {
  // What the start waited on was about the folder and access it had. A field left as it was, which its blur still
  // saves, changes neither
  const content = pendingCard(cardId)?.content;
  const changed = Object.entries(change).some(
    ([field, value]) => content?.[field as keyof typeof change] !== value
  );
  if (changed) runInAction(() => startQuestions.delete(cardId));
  return inTurn(async () => {
    const content = pendingCard(cardId)?.content;
    if (content)
      await actionCardCommands.saveDraft(cardId, { ...content, ...change });
  });
};

const runFor = (
  session: AgentSession,
  current: CodingAgentContent
): Run | null => {
  const run = { session_id: session.sessionId };
  switch (session.state) {
    case 'working':
      return { ...run, state: 'working' };
    case 'finished':
      return { ...run, state: 'finished', summary: session.message };
    case 'failed':
      return { ...run, state: 'failed', error: session.message };
    case 'stopped':
      return { ...run, state: 'stopped' };
    case 'ended':
      // Closed without saying how the turn went
      return isCodingAgentRunning(current)
        ? { ...run, state: 'finished' }
        : null;
    case 'idle':
      return null;
  }
};

// The cards the main process has reported a run for since this window opened
const reportedCards = new Set<string>();

const writeRun = async (session: AgentSession): Promise<void> => {
  if (!session.cardId) return;
  reportedCards.add(session.cardId);
  await saveRun(session.cardId, (content) => {
    const ran = content.run && content.session_id;
    // Only a session that starts working takes the card over. An older session's later report is not this run's
    const stale =
      ran && ran !== session.sessionId && session.state !== 'working';
    return stale ? null : runFor(session, content);
  });
};

// After a restart the main process knows none of the earlier runs: quitting ended them
const reconcile = async (): Promise<void> => {
  const sessions = await electronApis.codingAgents.listSessions();
  await thisDevice();
  for (const session of sessions) await writeRun(session);
  // Reported runs are read as the scan goes, so one reported while it waits is not taken for lost
  const lost = (cardId: string, content: CodingAgentContent) =>
    isCodingAgentRunning(content) && !reportedCards.has(cardId);
  for (const card of littlebirdSdk.stores.actionCards.orderedData) {
    const content = pendingCard(card.id)?.content;
    const run = content?.run;
    if (!run || !lost(card.id, content) || runsElsewhere(run)) continue;
    const failed = {
      ...run,
      state: 'failed',
      error: CLOSED_BEFORE_FINISH,
    } as const;
    await saveRun(card.id, (now) => (lost(card.id, now) ? failed : null));
  }
};

export const startCodingAgent = async (
  cardId: string,
  {
    startAnyway = false,
    trustFolder = false,
    newSession = false,
  }: StartOptions = {}
): Promise<void> => {
  // Read after the writes under way, so the agent starts with the folder and access the user last set
  const content = await inTurn(async () => pendingCard(cardId)?.content);
  if (!content || !canStartCodingAgent(content) || startingCards.has(cardId)) {
    return;
  }
  runInAction(() => {
    startingCards.add(cardId);
    startQuestions.delete(cardId);
  });
  try {
    const continued = newSession ? null : codingAgentSessionToContinue(content);
    const result = await electronApis.codingAgents.launch({
      agent: content.agent,
      cwd: content.cwd,
      prompt: content.prompt,
      access: content.access,
      resumeSessionId: continued ?? undefined,
      cardId,
      startAnyway,
      trustFolder,
    });
    // From here the main process reports the run under the card's id
    if (result.success) return;
    const choice = result.busy
      ? 'startAnyway'
      : result.untrustedFolder
        ? 'trustFolder'
        : undefined;
    if (choice) {
      const given = { startAnyway, trustFolder, newSession };
      runInAction(() =>
        startQuestions.set(cardId, { error: result.error, choice, given })
      );
      return;
    }
    await saveRun(cardId, () => ({ state: 'failed', error: result.error }));
  } catch (error) {
    logError(error, 'Could not start a coding agent');
    toast.error('Littlebird could not start the coding agent. Try again.');
  } finally {
    runInAction(() => startingCards.delete(cardId));
  }
};

const onSession = async (
  cardId: string,
  act: 'stop' | 'openSession',
  allowed: (content: CodingAgentContent) => boolean
): Promise<void> => {
  const content = codingCard(cardId)?.content;
  const sessionId = content?.run && content.session_id;
  if (!content || !sessionId || !allowed(content)) return;
  const session = { agent: content.agent, sessionId, cwd: content.cwd };
  const result = await electronApis.codingAgents[act](session);
  if (!result.success) toast.error(result.error);
};

export const stopCodingAgent = (cardId: string) =>
  onSession(cardId, 'stop', isCodingAgentRunning);

export const openCodingAgent = (cardId: string) =>
  onSession(cardId, 'openSession', canOpenCodingAgent);

export const initializeCodingAgentRuns = (): void => {
  if (!inElectron()) return;
  void thisDevice();
  const logged = (work: Promise<void>) =>
    void work.catch((error) =>
      logError(error, 'Could not record a coding agent run')
    );
  electronApis.codingAgents.onSessionChanged((session) =>
    logged(writeRun(session))
  );
  globalEventBus.on(EventNames.BOOTSTRAP_COMPLETE, () => logged(reconcile()));
};
